import { MapRenderer } from './map-renderer.js';
import { buildWalkableSet, versioned } from './tile-utils.js';

const GAME = '/game';

// The authored walkable polygon occupies a small rectangle in Saffron. Keep
// the original coordinate system (so DOM roofs/NPCs stay aligned) while only
// sending this box plus a visual margin through the Pixi placement pipeline.
export const CITY_RENDER_BOUNDS = Object.freeze({ minX: 1005, maxX: 1066, minY: 1014, maxY: 1074 });

/**
 * Telhados que somem quando o jogador entra. São os mesmos retângulos que o
 * pipeline usa para recortar os overlays WebP (tools/lib/map-regions.js, regiões
 * `saffron-pc-roof` e `saffron-shop-roof`) — mantenha os dois lados iguais.
 */
export const CITY_ROOF_RECTS = Object.freeze({
  pc: Object.freeze({ minX: 1026, maxX: 1046, minY: 1019, maxY: 1039 }),
  shop: Object.freeze({ minX: 1008, maxX: 1024, minY: 1043, maxY: 1063 }),
});

export function cropCityMapDoc(mapDoc, bounds = CITY_RENDER_BOUNDS) {
  const tiles = (mapDoc?.tiles || []).filter(([x, y]) => x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY);
  return { ...mapDoc, tiles };
}

async function fetchJson(path) {
  const res = await fetch(versioned(path));
  if (!res.ok) throw new Error(`Falha ao carregar ${path}`);
  return res.json();
}

// O renderer de tiles (Pixi) é o padrão: é ele que desenha o mapa tile a tile.
// O caminho PNG é só o fallback de emergência (uma imagem única esticada), e
// continua acessível via ?renderer=png para depuração.
export function isPixiRendererEnabled() {
  const params = new URLSearchParams(location.search);
  if (params.get('renderer') === 'pixi') {
    localStorage.removeItem('idlemon-renderer');
    return true;
  }
  if (params.get('renderer') === 'png') {
    localStorage.setItem('idlemon-renderer', 'png');
    return false;
  }
  // Preferência antiga de debug: se ficou presa em png, o mapa volta à
  // foto única. Limpa automaticamente — use ?renderer=png só quando quiser o fallback.
  if (localStorage.getItem('idlemon-renderer') === 'png') {
    localStorage.removeItem('idlemon-renderer');
  }
  return true;
}

export async function initCityPixi(host, { boundary, pointInPolygon }) {
  const [mapDoc, collision, drawOrder, version] = await Promise.all([
    fetchJson(`${GAME}/maps/saffron.json`),
    fetchJson(`${GAME}/collision.json`),
    fetchJson(`${GAME}/draworder.json`),
    fetchJson(`${GAME}/asset-packs/version.json`),
  ]);

  const manifest = await fetchJson(`${GAME}/asset-packs/${version.items}`);
  const renderer = new MapRenderer(host);
  // viewport:true é o que liga o culling de chunks. Sem ele o canvas nascia do
  // tamanho do mundo (11280x11040 = 124 megapixels, ~475 MB de framebuffer) e
  // os 27.8 mil sprites do mapa eram desenhados todo frame, com o navegador
  // ainda tendo que compor essa camada gigante a cada passo do jogador — era o
  // travamento exclusivo da cidade. A rota já subia com viewport:true.
  const renderMap = cropCityMapDoc(mapDoc);
  await renderer.init({
    mapDoc: renderMap, manifest, drawOrder, viewport: true, clampCamera: false,
    roofRects: CITY_ROOF_RECTS,
  });

  const walkable = buildWalkableSet(renderMap, collision.blocking || []);

  return {
    renderer,
    walkable(tx, ty) {
      if (!pointInPolygon(tx + 0.5, ty + 0.5, boundary)) return false;
      return walkable.has(`${tx},${ty}`);
    },
    setToppersVisible(visible) {
      renderer.setToppersVisible(visible);
    },
    /** `group` = 'pc' | 'shop' | 'static'. */
    setRoofVisible(group, visible) {
      renderer.setRoofVisible(group, visible);
    },
    setCameraPercent(xPercent, yPercent) {
      return renderer.setCameraPercent(xPercent, yPercent);
    },
    destroy() {
      renderer.destroy();
    },
  };
}
